import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Target, TrendingUp, DollarSign, CheckCircle, ArrowRight, ArrowLeft, ChevronRight,
  Layers, Calculator, BarChart3, Save, RefreshCw, Download,
  Info, AlertTriangle, CheckCircle2, XCircle, Plus, Minus, Loader2,
  GitCompare, Trophy, Scale, Compass, Sparkles, ChevronDown, ChevronUp,
  FileText, HelpCircle, Mail, X, Send, Award, Users, Edit3
} from 'lucide-react';

// Import extracted components
import {
  API_URL,
  KPI_ICONS,
  MODALITY_ICONS,
  AchievementsBadge,
  AchievementsModal,
  StepIndicator,
  KPICard,
  TemplateCard,
  InterventionCard,
  ModalityCard,
  InterventionConfigPanel,
  ScenarioComparisonResults,
  ROIResults,
  CalculationDetails,
  StepKPISelection,
  StepChooseInterventions,
  StepConfigureModel
} from '../components/solution-finder';

import CohortSelector from '../components/CohortSelector';
import logger from '../lib/logger';
import { toast } from 'sonner';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import SolutionFinderContextStack from '../components/solution-finder/SolutionFinderContextStack';
import SolutionOpportunityCard from '../components/impact/SolutionOpportunityCard';
import { getConstraintContext } from '../lib/constraintContext';
import { readActiveProvenance } from '../lib/solutionFinderProvenance';
import { computeChainLeak } from '../lib/chainLeak';
import { HealthStatsStrip } from '../components/common/HealthStatsStrip';
import { getLightSectionTheme } from '../lib/sectionThemes';

// Capability Inventory bridge — map a skill's family/type to the mobility-KPI
// interventions most likely to build it, so arriving with a member's skill gap
// auto-selects the right L&D interventions (1-click "close this gap").
const CAP_SKILL_FAMILY_INTERVENTIONS = {
  'Leadership & People': ['leadership_dev', 'coaching_mentoring'],
  'Risk & Compliance': ['performance_mgmt', 'coaching_mentoring'],
  'Commercial & Customer': ['coaching_mentoring', 'performance_mgmt'],
};
const CAP_SKILL_TYPE_INTERVENTIONS = {
  technical: ['coaching_mentoring', 'onboarding'],
  durable: ['coaching_mentoring', 'performance_mgmt'],
};
const mapSkillToInterventions = (family, type) =>
  CAP_SKILL_FAMILY_INTERVENTIONS[family] || CAP_SKILL_TYPE_INTERVENTIONS[type] || ['coaching_mentoring'];

// Main Solution Finder Page
const SolutionFinderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Round-trip context from Business Case Builder. When non-null,
  // recalc + Export will return to BCB to update the original case.
  const [prefillContext, setPrefillContext] = useState(null);
  // List of saved cases for the "Start from existing" picker on
  // step 0. Lazy-fetched on mount.
  const [existingCases, setExistingCases] = useState([]);
  const [showExistingPicker, setShowExistingPicker] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  
  // Ref for scrolling to templates
  const templatesRef = useRef(null);
  
  // User analytics
  const [userStats, setUserStats] = useState({ analyses_this_month: 0, saved_scenarios: 0 });
  
  // Achievements/Gamification
  const [achievements, setAchievements] = useState(null);
  const [showAchievementsModal, setShowAchievementsModal] = useState(false);
  const [newBadgeNotification, setNewBadgeNotification] = useState(null);
  
  // Data
  const [kpis, setKpis] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  // Selections
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [selectedInterventions, setSelectedInterventions] = useState({});
  const [configuringIntervention, setConfiguringIntervention] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Results
  const [roiResults, setRoiResults] = useState(null);
  
  // Comparison state
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [scenarioB, setScenarioB] = useState(null);
  
  // View toggle for Step 0
  const [showTemplates, setShowTemplates] = useState(true);
  
  // Cohort selection for tracking
  const [selectedCohort, setSelectedCohort] = useState(null);
  
  // Scenario config
  const [scenarioConfig, setScenarioConfig] = useState({
    time_horizon_months: 12,
    implementation_lag_months: 2,
    ramp_up_months: 3,
    include_decay: false,
    annual_decay_rate: 0.15
  });
  
  const steps = ['Identify Constraint', 'Select Intervention', 'Model Value Recovery', 'Compare Outcomes'];

  // Live Mission Control constraint (chain leak + maturity-attenuated
  // recovery). Powers the Current Highest Opportunity card and the
  // "Recommended for your constraint" template highlights.
  const [healthDeltas, setHealthDeltas] = useState(null);
  const [maturityFin, setMaturityFin] = useState(null);
  useEffect(() => {
    const token = secureStorage.getItem('session_token');
    if (!token) return undefined;
    let cancelled = false;
    const fetchInto = async (path, set) => {
      try {
        const res = await authedFetch(`${API_URL}/api${path}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok && !cancelled) set(await res.json());
      } catch { /* opportunity card self-hides */ }
    };
    fetchInto('/health/quarter-deltas', setHealthDeltas);
    fetchInto('/maturity-assessment/financial', setMaturityFin);
    return () => { cancelled = true; };
  }, []);

  const chainLeak = computeChainLeak(healthDeltas?.metrics);
  const leakPct = chainLeak ? Math.abs(Math.round(chainLeak.deltaPct)) : null;
  const realizedPct = maturityFin?.has_assessment
    ? (maturityFin?.investment_efficiency?.current_pct
        ?? (maturityFin?.maturity_factor?.current != null ? maturityFin.maturity_factor.current * 100 : null))
    : null;
  const leakAttenuated = typeof realizedPct === 'number' && realizedPct > 0;
  const leakRecoveryPct = chainLeak
    ? (leakAttenuated ? Math.max(1, Math.round(leakPct * (realizedPct / 100))) : leakPct)
    : null;

  // Consume prefill from Business Case Builder round-trip.
  // We seed selectedKPI / selectedInterventions / cohort / scenario
  // and jump to step 2 (Configure & Model) so the user can review &
  // tweak inputs before recalculating.
  useEffect(() => {
    const prefill = location.state?.prefillFromCase;
    if (!prefill) return;
    setPrefillContext(prefill);
    if (prefill.kpi) setSelectedKPI(prefill.kpi);
    if (prefill.cohort) setSelectedCohort(prefill.cohort);
    if (prefill.scenario_config) {
      setScenarioConfig(prev => ({ ...prev, ...prefill.scenario_config }));
    }
    if (Array.isArray(prefill.interventions) && prefill.interventions.length) {
      const keyed = {};
      prefill.interventions.forEach(iv => {
        const id = iv.intervention_id || iv.id;
        if (id) {
          keyed[id] = {
            intervention_id: id,
            modality_id: iv.modality_id || 'in_person_ilt',
            target_audience_size: iv.target_audience_size ?? 100,
            cost_override: iv.cost_override ?? null,
            ...iv,
          };
        }
      });
      setSelectedInterventions(keyed);
    }
    // Jump to Configure & Model so the user can adjust hours/audience/cost
    setCurrentStep(2);
    // Clear navigation state so a refresh doesn't re-prefill
    window.history.replaceState({}, '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Fetch KPIs on mount
  useEffect(() => {
    const fetchKPIs = async () => {
      setLoading(true);
      try {
        const response = await authedFetch(`${API_URL}/api/solution-finder/kpis`);
        const data = await response.json();
        setKpis(data.kpis || []);
      } catch (error) {
        logger.error('Error fetching KPIs:', error);
      }
      setLoading(false);
    };
    fetchKPIs();
  }, []);

  // Load saved cases for the "Start from existing" picker. We only
  // surface cases that have an actual calculated solution (non-empty
  // financial_summary) — cases without interventions would load into
  // the wizard as an empty shell and make Calculate ROI pointless.
  useEffect(() => {
    const fetchExisting = async () => {
      try {
        const token = secureStorage.getItem('session_token');
        const r = await authedFetch(`${API_URL}/api/business-case-builder/list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (r.ok) {
          const d = await r.json();
          const hydratable = (d.cases || []).filter(c => {
            const fs = c?.solution_data_summary?.financial_summary || {};
            const ic = c?.solution_data_summary?.interventions_count || 0;
            // A case is only useful to "start from" if it has a
            // computed outcome (roi_percent / kpi_uplift_percent)
            // OR is a draft/stub that at least has interventions
            // selected — otherwise loading it would dump the user
            // into an empty wizard.
            return fs.roi_percent != null || fs.kpi_uplift_percent != null || ic > 0;
          });
          setExistingCases(hydratable);
        }
      } catch { /* silent */ }
    };
    fetchExisting();
  }, []);

  // Seed state from an existing saved case. Calls /{case_id} for
  // the full solution_data, seeds wizard state, jumps to the
  // earliest step with missing data (so a partial case still works).
  // Doesn't bind to the original caseId so a downstream save
  // creates a fresh case rather than overwriting.
  const handleStartFromExisting = async (caseId) => {
    try {
      const token = secureStorage.getItem('session_token');
      const r = await authedFetch(`${API_URL}/api/business-case-builder/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!r.ok) throw new Error('failed');
      const c = await r.json();
      const sd = c.solution_data || {};
      const hasKpi = !!(sd.kpi && sd.kpi.id);
      const ivList = Array.isArray(sd.interventions) ? sd.interventions : [];
      const hasInterventions = ivList.length > 0;

      if (hasKpi) setSelectedKPI(sd.kpi);
      if (sd.cohort) setSelectedCohort(sd.cohort);
      if (sd.scenario_config) setScenarioConfig(prev => ({ ...prev, ...sd.scenario_config }));

      if (hasInterventions) {
        const keyed = {};
        ivList.forEach(iv => {
          const id = iv.intervention_id || iv.id;
          if (id) {
            keyed[id] = {
              intervention_id: id,
              modality_id: iv.modality_id || 'in_person_ilt',
              target_audience_size: iv.target_audience_size ?? 100,
              cost_override: iv.cost_override ?? null,
              ...iv,
            };
          }
        });
        setSelectedInterventions(keyed);
      } else {
        // Reset any stale interventions so the wizard doesn't carry
        // over the previous selection.
        setSelectedInterventions({});
      }

      setShowExistingPicker(false);

      // Route to the first meaningful step: if interventions are
      // missing we cannot calculate, so send the user back to
      // "Choose Interventions". Otherwise land on Configure & Model.
      if (hasKpi && hasInterventions) {
        setCurrentStep(2);
        toast.success('Loaded case — review inputs then recalculate');
      } else if (hasKpi) {
        setCurrentStep(1);
        toast.info('Loaded case — please select interventions to continue');
      } else {
        setCurrentStep(0);
        toast.info('Loaded case — select a KPI to continue');
      }
    } catch (err) {
      logger.error('Failed to start from existing:', err);
      toast.error('Could not load that case');
    }
  };
  
  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await authedFetch(`${API_URL}/api/solution-finder/templates`);
        const data = await response.json();
        setTemplates(data.templates || []);
      } catch (error) {
        logger.error('Error fetching templates:', error);
      }
    };
    fetchTemplates();
  }, []);
  
  // Fetch user statistics (analyses this month + saved scenarios)
  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        const token = secureStorage.getItem('session_token');
        if (!token) return;
        
        // Saved scenarios — also drives "analyses this month" (count
        // of scenarios created in the current calendar month).
        const scenariosResponse = await authedFetch(`${API_URL}/api/scenarios`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (!scenariosResponse.ok) return;
        const scenariosData = await scenariosResponse.json();
        const list = Array.isArray(scenariosData) ? scenariosData : (scenariosData.scenarios || []);
        const now = new Date();
        const analysesCount = list.filter(s => {
          const d = new Date(s.created_at);
          return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        }).length;
        
        setUserStats({
          analyses_this_month: analysesCount,
          saved_scenarios: list.length
        });
      } catch (error) {
        logger.error('Error fetching user stats:', error);
      }
    };
    fetchUserStats();
  }, []);
  
  // Fetch achievements on mount
  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        const response = await authedFetch(`${API_URL}/api/achievements/user-progress`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setAchievements(data);
        }
      } catch (error) {
        logger.error('Error fetching achievements:', error);
      }
    };
    fetchAchievements();
  }, []);
  
  // Track activity and check for new badges
  const trackActivity = useCallback(async (activityType, data = {}) => {
    try {
      const response = await authedFetch(`${API_URL}/api/achievements/track-activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ activity_type: activityType, data })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Show notification for new badges
        if (result.new_badges && result.new_badges.length > 0) {
          setNewBadgeNotification(result.new_badges[0]);
          setTimeout(() => setNewBadgeNotification(null), 5000);
          
          // Refresh achievements
          const achievementsResponse = await authedFetch(`${API_URL}/api/achievements/user-progress`, {
            credentials: 'include'
          });
          if (achievementsResponse.ok) {
            const data = await achievementsResponse.json();
            setAchievements(data);
          }
        }
      }
    } catch (error) {
      logger.error('Error tracking activity:', error);
    }
  }, []);
  
  // Fetch modalities on mount
  useEffect(() => {
    const fetchModalities = async () => {
      try {
        const response = await authedFetch(`${API_URL}/api/solution-finder/modalities`);
        const data = await response.json();
        setModalities(data.modalities || []);
      } catch (error) {
        logger.error('Error fetching modalities:', error);
      }
    };
    fetchModalities();
  }, []);
  
  // Fetch interventions when KPI is selected
  useEffect(() => {
    if (!selectedKPI) return;
    
    const fetchInterventions = async () => {
      setLoading(true);
      try {
        const response = await authedFetch(`${API_URL}/api/solution-finder/kpis/${selectedKPI.id}/interventions`);
        const data = await response.json();
        setInterventions(data.interventions || []);
      } catch (error) {
        logger.error('Error fetching interventions:', error);
      }
      setLoading(false);
    };
    fetchInterventions();
  }, [selectedKPI]);
  
  const handleKPISelect = (kpi) => {
    setSelectedKPI(kpi);
    setSelectedInterventions({});
    setRoiResults(null);
    setSelectedTemplate(null);
  };

  // Benchmarking gap bridge — arriving with ?kpi=<id> (e.g. from the
  // Navigation Advantage "Largest Gap" link) pre-selects that KPI and
  // advances to intervention selection so the user lands mid-flow.
  const kpiPrefillDone = useRef(false);
  useEffect(() => {
    if (kpiPrefillDone.current || kpis.length === 0) return;
    const kpiParam = new URLSearchParams(window.location.search).get('kpi');
    if (!kpiParam) { kpiPrefillDone.current = true; return; }
    const match = kpis.find(k => k.id === kpiParam);
    if (match) {
      kpiPrefillDone.current = true;
      handleKPISelect(match);
      setCurrentStep(1);
    }
  }, [kpis]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capability Inventory bridge — arriving with ?cap_member= (from the
  // Capability Inventory succession bench / what-if backfill / member drawer)
  // fetches that member's upskilling plan and auto-selects the interventions
  // mapped to their specific missing skills, so the user lands on Select
  // Intervention with the right L&D interventions already ticked.
  const capPreselectDone = useRef(false);
  useEffect(() => {
    if (capPreselectDone.current) return;
    if (!selectedKPI || interventions.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const memberId = params.get('cap_member');
    if (!memberId) { capPreselectDone.current = true; return; }
    capPreselectDone.current = true;
    const orgId = params.get('organization_id');
    const roleId = params.get('cap_role_id');
    (async () => {
      try {
        const token = secureStorage.getItem('session_token');
        const q = new URLSearchParams();
        if (orgId) q.set('organization_id', orgId);
        if (roleId) q.set('role_id', roleId);
        const res = await authedFetch(
          `${API_URL}/api/capability-inventory/members/${memberId}/upskilling-plan?${q.toString()}`,
          { headers: { 'Authorization': `Bearer ${token}` } },
        );
        if (!res.ok) return;
        const plan = await res.json();
        // Prefer the server-resolved recommendation (honors the org's
        // configurable skill→intervention map); fall back to the local
        // heuristic if the field is absent (older backend).
        let toSelect;
        const available = new Set(interventions.map(i => i.id));
        if (Array.isArray(plan.recommended_intervention_ids) && plan.recommended_intervention_ids.length) {
          toSelect = plan.recommended_intervention_ids.filter(id => available.has(id));
        } else {
          const wanted = new Set();
          (plan.targets || []).forEach(t => (t.gaps || []).forEach(g => {
            mapSkillToInterventions(g.skill_family, g.skill_type).forEach(id => wanted.add(id));
          }));
          toSelect = [...wanted].filter(id => available.has(id));
        }
        if (toSelect.length === 0) return;
        const keyed = {};
        toSelect.forEach(id => {
          keyed[id] = { intervention_id: id, modality_id: 'in_person_ilt', target_audience_size: 100, cost_override: null };
        });
        setSelectedInterventions(keyed);
        toast.success(
          `Pre-selected ${toSelect.length} intervention${toSelect.length !== 1 ? 's' : ''} mapped to ${plan.name}'s skill gaps — review and Calculate ROI`,
        );
      } catch { /* silent — user can still pick manually */ }
    })();
  }, [interventions, selectedKPI]);
  // Handle template selection - loads template and pre-populates
  const handleTemplateSelect = async (template) => {
    setLoadingTemplate(true);
    try {
      // Track template usage
      authedFetch(`${API_URL}/api/solution-finder/templates/${template.id}/track`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${secureStorage.getItem('session_token')}`,
          'Content-Type': 'application/json'
        }
      }).catch(() => {});
      
      // Track for achievements
      trackActivity('template_used', { template_id: template.id });
      
      const response = await authedFetch(`${API_URL}/api/solution-finder/templates/${template.id}`);
      const data = await response.json();
      
      if (data.template && data.kpi) {
        // Set the KPI
        setSelectedKPI(data.kpi);
        setSelectedTemplate(data.template);
        
        // Set scenario config from template
        if (data.template.scenario_config) {
          setScenarioConfig(prev => ({
            ...prev,
            ...data.template.scenario_config
          }));
        }
        
        // Pre-populate interventions after fetching intervention list
        const intResponse = await authedFetch(`${API_URL}/api/solution-finder/kpis/${data.kpi.id}/interventions`);
        const intData = await intResponse.json();
        setInterventions(intData.interventions || []);
        
        // Set selected interventions from template
        const selections = {};
        for (const intConfig of data.template.interventions) {
          selections[intConfig.intervention_id] = {
            intervention_id: intConfig.intervention_id,
            modality_id: intConfig.modality_id,
            target_audience_size: intConfig.target_audience_size,
            cost_override: null
          };
        }
        setSelectedInterventions(selections);
        
        // Go to step 1 (Choose Interventions) to review/modify pre-selected interventions
        // This keeps the flow consistent whether user chose a template or individual KPI
        setCurrentStep(1);
      }
    } catch (error) {
      logger.error('Error loading template:', error);
    }
    setLoadingTemplate(false);
  };
  
  const handleInterventionToggle = (interventionId) => {
    setSelectedInterventions(prev => {
      const newSelections = { ...prev };
      if (newSelections[interventionId]) {
        delete newSelections[interventionId];
      } else {
        newSelections[interventionId] = {
          intervention_id: interventionId,
          modality_id: 'in_person_ilt',
          target_audience_size: 100,
          cost_override: null
        };
      }
      return newSelections;
    });
  };
  
  const handleConfigChange = (interventionId, config) => {
    setSelectedInterventions(prev => ({
      ...prev,
      [interventionId]: config
    }));
  };
  
  const calculateROI = async () => {
    const token = secureStorage.getItem('session_token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    setCalculating(true);
    try {
      const response = await authedFetch(`${API_URL}/api/solution-finder/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          kpi_id: selectedKPI.id,
          interventions: Object.values(selectedInterventions),
          cohort_id: selectedCohort?.id || null,
          cohort_name: selectedCohort?.name || null,
          ...scenarioConfig
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setRoiResults(data);
        setCurrentStep(3);
        
        // Track ROI calculation
        authedFetch(`${API_URL}/api/solution-finder/track-event`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            event_type: 'roi_calculated',
            metadata: {
              kpi_id: selectedKPI.id,
              template_id: selectedTemplate?.id || null,
              cohort_id: selectedCohort?.id || null,
              cohort_name: selectedCohort?.name || null,
              interventions_count: Object.keys(selectedInterventions).length,
              roi_percent: data.roi_analysis?.roi_percent,
              total_investment: data.roi_analysis?.total_investment
            }
          })
        }).catch(() => {});
        
        // Track for achievements
        trackActivity('analysis');
      } else {
        logger.error('Failed to calculate ROI');
      }
    } catch (error) {
      logger.error('Error calculating ROI:', error);
    }
    setCalculating(false);
  };
  
  const saveSolution = async () => {
    const token = secureStorage.getItem('session_token');
    if (!token || !roiResults) return;
    
    setSaving(true);
    try {
      const response = await authedFetch(`${API_URL}/api/solution-finder/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: `${selectedKPI.name} Solution${selectedCohort ? ` - ${selectedCohort.name}` : ''}`,
          description: `Solution to address: ${selectedKPI.issue_statement}${selectedCohort ? ` for cohort: ${selectedCohort.name}` : ''}`,
          kpi_id: selectedKPI.id,
          cohort_id: selectedCohort?.id || null,
          cohort_name: selectedCohort?.name || null,
          interventions: Object.values(selectedInterventions),
          result: roiResults,
          constraint_context: getConstraintContext()
        })
      });
      
      if (response.ok) {
        alert('Solution saved successfully!');
      }
    } catch (error) {
      logger.error('Error saving solution:', error);
    }
    setSaving(false);
  };

  // Save the current wizard state as a draft business case so the
  // user doesn't lose their work mid-flow. Landed as a stub in
  // business_cases with is_draft=true; the Solution Finder
  // "start from existing" picker surfaces it so the user can
  // resume later. We insert a fresh case every call (no update
  // semantics) — keeps the draft workflow predictable.
  const saveDraft = async () => {
    const token = secureStorage.getItem('session_token');
    if (!token || !selectedKPI) return;
    setSaving(true);
    try {
      const interventionsList = Object.values(selectedInterventions);
      const titleParts = [selectedKPI.name];
      if (selectedCohort?.name) titleParts.push(selectedCohort.name);
      titleParts.push('Draft');
      const response = await authedFetch(`${API_URL}/api/business-case-builder/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: `${titleParts.join(' — ')}`,
          description: `Draft captured before ROI calculation for ${selectedKPI.name}`,
          sections: [],
          solution_data: {
            kpi: selectedKPI,
            cohort: selectedCohort || null,
            interventions: interventionsList,
            scenario_config: scenarioConfig,
            is_draft: true,
          },
        })
      });
      if (response.ok) {
        const d = await response.json();
        toast.success('Draft saved — resume anytime from "Start from existing case"');
        // Refresh picker list so the draft shows up immediately
        try {
          const r = await authedFetch(`${API_URL}/api/business-case-builder/list`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (r.ok) {
            const data = await r.json();
            const hydratable = (data.cases || []).filter(c => {
              const fs = c?.solution_data_summary?.financial_summary || {};
              const ic = c?.solution_data_summary?.interventions_count || 0;
              return fs.roi_percent != null || fs.kpi_uplift_percent != null || ic > 0;
            });
            setExistingCases(hydratable);
          }
        } catch { /* non-fatal */ }
        return d.id;
      }
      toast.error('Could not save draft');
    } catch (error) {
      logger.error('Error saving draft:', error);
      toast.error('Could not save draft');
    } finally {
      setSaving(false);
    }
  };
  
  const exportBusinessCase = () => {
    // Stamp the active narrative-bridge provenance into the
    // navigation state so BCB save can persist it onto the case.
    const provenance = readActiveProvenance({ kpis });
    // Navigate to business case builder with the solution data
    navigate('/business-case-builder', { 
      state: { 
        solutionData: {
          ...roiResults,
          // Mission Control constraint framing → flows into the
          // generated "Investment & ROI" / exec-summary sections and
          // all exports (PDF/Word/PPT/one-page summary).
          constraint_context: chainLeak ? {
            stage: chainLeak.key,
            label: chainLeak.stage.label,
            leak: leakPct,
            recovery: leakRecoveryPct,
            attenuated: leakAttenuated,
          } : getConstraintContext(),
          cohort: selectedCohort ? {
            id: selectedCohort.id,
            name: selectedCohort.name,
            demographics: selectedCohort.demographics || null
          } : roiResults?.cohort || null,
          // Narrative-bridge provenance — surfaces as badges in the
          // BCB tile + Compare Scenarios after the case is saved.
          provenance,
        },
        kpi: selectedKPI,
        interventions: Object.values(selectedInterventions),
        provenance,
        // Round-trip context so BCB knows to update the original
        // saved case rather than create a new one.
        ...(prefillContext?.caseId ? {
          originalCaseId: prefillContext.caseId,
          originalCaseTitle: prefillContext.caseTitle,
        } : {})
      } 
    });
  };
  
  const openComparisonModal = () => {
    // Initialize Scenario B with a copy of current config but different modality
    const currentInterventions = Object.values(selectedInterventions);
    const scenarioBInterventions = currentInterventions.map(int => ({
      ...int,
      // Try to use a different modality - if it's in_person_ilt, suggest virtual_ilt
      modality_id: int.modality_id === 'in_person_ilt' ? 'virtual_ilt' : 
                   int.modality_id === 'virtual_ilt' ? 'blended' : 'in_person_ilt'
    }));
    
    setScenarioB({
      interventions: scenarioBInterventions,
      config: { ...scenarioConfig }
    });
    setShowComparisonModal(true);
  };
  
  const runComparison = async () => {
    if (!scenarioB) return;
    
    const token = secureStorage.getItem('session_token');
    if (!token) return;
    
    setComparing(true);
    try {
      const response = await authedFetch(`${API_URL}/api/solution-finder/compare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          scenario_a: {
            kpi_id: selectedKPI.id,
            interventions: Object.values(selectedInterventions),
            ...scenarioConfig
          },
          scenario_b: {
            kpi_id: selectedKPI.id,
            interventions: scenarioB.interventions,
            ...scenarioB.config
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setComparisonResults(data);
        setShowComparisonModal(false);
      }
    } catch (error) {
      logger.error('Error comparing scenarios:', error);
    }
    setComparing(false);
  };
  
  const updateScenarioBIntervention = (index, updates) => {
    setScenarioB(prev => {
      const newInterventions = [...prev.interventions];
      newInterventions[index] = { ...newInterventions[index], ...updates };
      return { ...prev, interventions: newInterventions };
    });
  };
  
  const canProceed = () => {
    switch (currentStep) {
      case 0: return selectedKPI !== null;
      case 1: return Object.keys(selectedInterventions).length > 0;
      case 2: {
        const entries = Object.values(selectedInterventions);
        // Must have at least one intervention, all fully configured —
        // otherwise Calculate ROI would POST an empty list and
        // produce a meaningless $0 result.
        return entries.length > 0 && entries.every(c => c.modality_id && c.target_audience_size > 0);
      }
      default: return true;
    }
  };
  
  const handleNext = () => {
    if (currentStep === 2) {
      calculateROI();
    } else {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };
  
  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };
  
  const __pageTheme = getLightSectionTheme('Navy');
  const __heroActions = (
    <>
      {achievements && (
        <AchievementsBadge
          achievements={achievements}
          showModal={showAchievementsModal}
          setShowModal={setShowAchievementsModal}
        />
      )}
      {currentStep === 0 && (
        <button
          onClick={() => {
            setShowTemplates(true);
            setTimeout(() => {
              templatesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white text-teal-700 rounded-lg font-semibold hover:bg-teal-50 transition-colors shadow-sm text-sm"
          data-testid="get-started-btn"
        >
          <Sparkles size={16} />
          Get Started
        </button>
      )}
    </>
  );
  const __heroStats = [
    { label: 'Analyses This Month', value: userStats.analyses_this_month, testId: 'sf-stat-analyses' },
    { label: 'Saved Scenarios', value: userStats.saved_scenarios, testId: 'sf-stat-saved' },
    { label: 'Quick-Start Templates', value: `${templates.length} Scenarios`, testId: 'sf-stat-templates' },
    { label: 'Export Ready', value: 'PDF • Word • PPT', testId: 'sf-stat-export' },
  ];
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Solution Finder"
        description="From Business Challenge to ROI-Validated Solution in Minutes. Start with a common business problem, select proven learning interventions, and instantly model the financial impact."
        theme={__pageTheme}
        icon={Compass}
        actions={__heroActions}
        stats={__heroStats}
        testId="solution-finder-hero"
      />
    )}>
    <div className="space-y-6" data-testid="solution-finder-page">
        {/* Unified context-stack — consolidates the four narrative-
            bridge banners (Mission Control constraint, business-case
            prefill, navigation-condition delta, industry-KPI gap) into
            a single visual rhythm with sessionStorage-backed dismiss
            persistence so users don't see the same banner twice. */}
        <SolutionFinderContextStack
          prefillContext={prefillContext}
          onBackToCase={() => navigate('/business-case-builder')}
          onDiscardPrefill={() => setPrefillContext(null)}
          kpis={kpis}
        />

        {/* Live org-pulse strip — Navigation Conditions interpretation
            of LEI / PE / ROE / PE Valid (+ QoQ deltas) so execs read
            readiness, adoption, conversion and evidence at a glance. */}
        <HealthStatsStrip
          title="Current Navigation Conditions"
          showInsights
          testId="sf-health-stats-strip"
        />

        {/* Step Indicator */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {steps.map((step, index) => (
              <div key={step.title || `step-${index}`} className="flex items-center">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                      index < currentStep
                        ? 'bg-teal-600 text-white'
                        : index === currentStep
                          ? 'bg-teal-600 text-white ring-2 ring-teal-300 ring-offset-2'
                          : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {index < currentStep ? <CheckCircle size={18} /> : index + 1}
                  </div>
                  <span className={`text-sm font-medium hidden md:block ${
                    index <= currentStep ? 'text-slate-800' : 'text-slate-400'
                  }`}>
                    {step}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <ChevronRight className={`mx-2 ${index < currentStep ? 'text-teal-600' : 'text-slate-300'}`} size={16} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          {/* Step 1: Select KPI */}
          {currentStep === 0 && (
            <div className="space-y-4">
              {/* Start-from-existing picker — fast lane for users who
                  want to clone a known-good case as the starting point. */}
              {existingCases.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white" data-testid="sf-start-from-existing">
                  <button
                    onClick={() => setShowExistingPicker(v => !v)}
                    className="w-full flex items-center justify-between p-3 hover:bg-slate-50 rounded-xl"
                    data-testid="sf-start-from-existing-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-blue-600" />
                      <span className="text-sm font-semibold text-slate-900">Start from an existing case</span>
                      <span className="text-[11px] text-slate-500">— pre-load inputs from a saved case ({existingCases.length} available)</span>
                    </div>
                    {showExistingPicker
                      ? <ChevronUp size={16} className="text-slate-400" />
                      : <ChevronDown size={16} className="text-slate-400" />}
                  </button>
                  {showExistingPicker && (
                    <div className="border-t border-slate-100 p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {existingCases.slice(0, 9).map(c => {
                        const fin = c.solution_data_summary?.financial_summary || {};
                        const kpiName = c.solution_data_summary?.kpi_name;
                        const isDraft = c.solution_data_summary?.is_draft;
                        const fmt = (v, d = 1) => v == null ? null : `${Number(v).toFixed(d)}%`;
                        const fmtRoiX = (v) => v == null ? null : `${(1 + Number(v) / 100).toFixed(1)}x`;
                        return (
                          <button
                            key={c.id}
                            onClick={() => handleStartFromExisting(c.id)}
                            className="text-left rounded-lg border border-slate-200 bg-white p-2.5 hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
                            data-testid={`sf-start-from-${c.id}`}
                          >
                            <div className="flex items-center gap-1.5">
                              <div className="text-sm font-semibold text-slate-900 truncate flex-1">{c.title}</div>
                              {isDraft && (
                                <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0" data-testid={`sf-draft-badge-${c.id}`}>
                                  Draft
                                </span>
                              )}
                            </div>
                            {kpiName && <div className="text-[11px] text-slate-500 truncate">{kpiName}</div>}
                            <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500">
                              {fin.kpi_uplift_percent != null && <span>KPI {fmt(fin.kpi_uplift_percent, 2)}</span>}
                              {fin.roi_percent != null && <span className="text-emerald-700 font-semibold">ROI {fmtRoiX(fin.roi_percent)}</span>}
                              {isDraft && fin.roi_percent == null && <span className="italic text-slate-400">Inputs only</span>}
                            </div>
                          </button>
                        );
                      })}
                      {existingCases.length > 9 && (
                        <div className="col-span-full text-[11px] text-slate-400 text-center py-1">
                          + {existingCases.length - 9} more — open Business Case Builder to browse all
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* Current Highest Opportunity — live Mission Control
                  constraint guiding template choice. Self-hides when
                  no chain stage declined this quarter. */}
              <SolutionOpportunityCard
                leak={chainLeak}
                leakPct={leakPct}
                recoveryPct={leakRecoveryPct}
                attenuated={leakAttenuated}
                templates={templates}
                onTemplateSelect={handleTemplateSelect}
                loadingTemplate={loadingTemplate}
              />
              <StepKPISelection
                templates={templates}
                kpis={kpis}
                loading={loading}
                loadingTemplate={loadingTemplate}
                showTemplates={showTemplates}
                setShowTemplates={setShowTemplates}
                selectedKPI={selectedKPI}
                handleKPISelect={handleKPISelect}
                handleTemplateSelect={handleTemplateSelect}
                templatesRef={templatesRef}
                constraintStage={chainLeak?.key}
              />
            </div>
          )}
          
          {/* Step 2: Choose Interventions */}
          {currentStep === 1 && (
            <StepChooseInterventions
              selectedKPI={selectedKPI}
              selectedTemplate={selectedTemplate}
              selectedInterventions={selectedInterventions}
              interventions={interventions}
              loading={loading}
              handleInterventionToggle={handleInterventionToggle}
              setConfiguringIntervention={setConfiguringIntervention}
              setSelectedInterventions={setSelectedInterventions}
            />
          )}
          
          {/* Step 3: Configure & Model */}
          {currentStep === 2 && (
            <StepConfigureModel
              selectedTemplate={selectedTemplate}
              selectedInterventions={selectedInterventions}
              interventions={interventions}
              modalities={modalities}
              scenarioConfig={scenarioConfig}
              setScenarioConfig={setScenarioConfig}
              setConfiguringIntervention={setConfiguringIntervention}
              setSelectedTemplate={setSelectedTemplate}
              setCurrentStep={setCurrentStep}
              setShowTemplates={setShowTemplates}
              selectedCohort={selectedCohort}
              setSelectedCohort={setSelectedCohort}
            />
          )}
          
          {/* Step 4: Review ROI */}
          {currentStep === 3 && (
            <ROIResults
              results={roiResults}
              onSave={saveSolution}
              onExport={exportBusinessCase}
              saving={saving}
              onCompare={openComparisonModal}
              comparing={comparing}
              selectedCohort={selectedCohort}
              constraint={chainLeak ? { label: chainLeak.stage.label, leak: leakPct } : null}
            />
          )}
        </div>
        
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={20} />
            Back
          </button>

          <div className="flex items-center gap-2">
            {/* Mid-flow draft save — visible while the user is still
                shaping inputs (before Review ROI). Requires at least a
                KPI + one intervention so we don't persist empty stubs. */}
            {currentStep < 3 && selectedKPI && Object.keys(selectedInterventions).length > 0 && (
              <button
                onClick={saveDraft}
                disabled={saving}
                data-testid="save-draft-btn"
                className="flex items-center gap-2 px-4 py-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg font-medium hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Save your current inputs as a draft you can resume later"
              >
                {saving ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <Save size={16} />
                )}
                Save Draft
              </button>
            )}

            {currentStep < 3 && (
              <button
                onClick={handleNext}
                disabled={!canProceed() || calculating}
                data-testid="next-step-btn"
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {calculating ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Calculating...
                  </>
                ) : currentStep === 2 ? (
                  <>
                    <Calculator size={20} />
                    Calculate ROI
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            )}
          </div>
          
          {currentStep === 3 && (
            <button
              onClick={() => {
                setCurrentStep(0);
                setSelectedKPI(null);
                setSelectedInterventions({});
                setRoiResults(null);
                setComparisonResults(null);
              }}
              className="flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
            >
              <RefreshCw size={20} />
              Start New Analysis
            </button>
          )}
        </div>
      </div>
      
      {/* Configuration Modal */}
      {configuringIntervention && selectedInterventions[configuringIntervention] && (
        <InterventionConfigPanel
          intervention={interventions.find(i => i.id === configuringIntervention)}
          modalities={modalities}
          config={selectedInterventions[configuringIntervention]}
          onConfigChange={(config) => handleConfigChange(configuringIntervention, config)}
          onClose={() => setConfiguringIntervention(null)}
        />
      )}
      
      {/* Comparison Configuration Modal */}
      {showComparisonModal && scenarioB && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <GitCompare size={28} />
                  <div>
                    <h3 className="text-xl font-bold">Configure Scenario B</h3>
                    <p className="text-blue-100">Set up an alternative scenario to compare with your current solution</p>
                  </div>
                </div>
                <button onClick={() => setShowComparisonModal(false)} className="p-2 hover:bg-slate-50 rounded-lg">
                  <XCircle size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Current Scenario A Summary */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">A</div>
                  Scenario A (Current)
                </h4>
                <div className="space-y-1 text-sm text-blue-800">
                  {Object.entries(selectedInterventions).map(([id, config]) => {
                    const intervention = interventions.find(i => i.id === id);
                    const modality = modalities.find(m => m.id === config.modality_id);
                    return (
                      <div key={id} className="flex items-center justify-between">
                        <span>{intervention?.name}</span>
                        <span className="text-blue-600">{modality?.name} • {config.target_audience_size} people</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Scenario B Configuration */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">B</div>
                  Scenario B (Configure)
                </h4>
                <div className="space-y-4">
                  {scenarioB.interventions.map((config, index) => {
                    const intervention = interventions.find(i => i.id === config.intervention_id);
                    return (
                      <div key={config.intervention_id || `int-${index}`} className="bg-white p-4 rounded-lg">
                        <h5 className="font-medium text-gray-900 mb-3">{intervention?.name}</h5>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm text-gray-500">Delivery Modality</label>
                            <select
                              value={config.modality_id}
                              onChange={(e) => updateScenarioBIntervention(index, { modality_id: e.target.value })}
                              className="w-full mt-1 px-3 py-2 border rounded-lg"
                            >
                              {modalities.map(m => (
                                <option key={m.id} value={m.id}>{m.name} ({(m.effectiveness * 100).toFixed(0)}% / {m.cost_multiplier}x)</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-sm text-gray-500">Audience Size</label>
                            <input
                              type="number"
                              value={config.target_audience_size}
                              onChange={(e) => updateScenarioBIntervention(index, { target_audience_size: parseInt(e.target.value) || 0 })}
                              className="w-full mt-1 px-3 py-2 border rounded-lg"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Quick Tips */}
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                  <Info size={18} />
                  Comparison Tips
                </h4>
                <ul className="text-sm text-amber-800 space-y-1">
                  <li>• Try a lower-cost modality to see ROI trade-offs</li>
                  <li>• Adjust audience size to find the optimal scale</li>
                  <li>• Compare In-Person vs Virtual for the same intervention</li>
                </ul>
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-4">
              <button
                onClick={() => setShowComparisonModal(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={runComparison}
                disabled={comparing}
                data-testid="run-comparison-btn"
                className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {comparing ? <Loader2 className="animate-spin" size={18} /> : <Scale size={18} />}
                Compare Scenarios
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Comparison Results Modal */}
      {comparisonResults && (
        <ScenarioComparisonResults
          comparisonData={comparisonResults}
          onClose={() => setComparisonResults(null)}
        />
      )}
      
      {/* Achievements Modal */}
      <AchievementsModal 
        isOpen={showAchievementsModal}
        onClose={() => setShowAchievementsModal(false)}
        achievements={achievements}
      />
      
      {/* New Badge Notification */}
      {newBadgeNotification && (
        <div className="fixed bottom-6 right-6 z-50 animate-bounce" data-testid="badge-notification">
          <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-xl shadow-2xl p-4 flex items-center gap-4 max-w-sm">
            <div className="p-3 bg-white border border-slate-200 rounded-lg">
              <Trophy size={28} />
            </div>
            <div>
              <p className="text-xs font-medium text-amber-100 uppercase">Badge Earned!</p>
              <p className="font-bold text-lg">{newBadgeNotification.name}</p>
              <p className="text-sm text-amber-100">{newBadgeNotification.description}</p>
              <p className="text-sm font-semibold mt-1">+{newBadgeNotification.points} points</p>
            </div>
            <button 
              onClick={() => setNewBadgeNotification(null)}
              className="text-slate-600 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default SolutionFinderPage;
